import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  FileText,
  Plus,
  Printer,
  Eye,
  Edit,
  Trash2,
  Send,
  CreditCard,
  XCircle,
  CheckCircle,
  Clock,
  AlertTriangle,
  Settings,
  Download,
  Loader2,
  Euro,
  Building2,
  User,
  Calendar,
  Percent,
  Package,
  Receipt
} from 'lucide-react';

const GermanInvoicesPage = () => {
  const { API_URL, getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const isRTL = language === 'ar' || language === 'ku';
  const printRef = useRef(null);

  // State
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Dialogs
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // Form data
  const [formData, setFormData] = useState({
    customer_id: '',
    custom_customer_name: '',
    custom_customer_address: '',
    invoice_type: 'rechnung',
    invoice_number: '', // Empty = auto-generate, or user can set custom
    invoice_date: format(new Date(), 'yyyy-MM-dd'),
    due_date: format(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    payment_method: 'ueberweisung',
    default_tax_rate: 7,
    notes: '',
    internal_notes: '',
    items: [{ description: '', quantity: 1, unit: 'Stück', unit_price: 0, discount_percent: 0, tax_rate: 7 }]
  });
  
  // Custom customer mode - default to manual entry
  const [useCustomCustomer, setUseCustomCustomer] = useState(true);

  // Settings form
  const [settingsForm, setSettingsForm] = useState({
    company_name: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    company_website: '',
    tax_number: '',
    ust_id: '',
    bank_name: '',
    iban: '',
    bic: '',
    account_holder: '',
    logo_base64: '',
    stamp_base64: '',
    signature_base64: '',
    footer_text: '',
    invoice_prefix: 'RE',
    geschaeftsfuehrer: ''
  });

  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: 'bar',
    notes: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invRes, custRes, prodRes, setRes, sumRes] = await Promise.all([
        axios.get(`${API_URL}/api/german-invoices`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/customers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/products`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/invoice-settings`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/german-invoices-summary`, { headers: getAuthHeader() })
      ]);
      setInvoices(invRes.data);
      setCustomers(custRes.data);
      setProducts(prodRes.data);
      setSettings(setRes.data);
      setSummary(sumRes.data);
      setSettingsForm(setRes.data);
    } catch (error) {
      console.error(error);
      toast.error('خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const fc = (n) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  // Status badge
  const getStatusBadge = (status) => {
    const config = {
      entwurf: { label: 'Entwurf', color: 'bg-gray-500', icon: Edit },
      offen: { label: 'Offen', color: 'bg-blue-500', icon: Clock },
      bezahlt: { label: 'Bezahlt', color: 'bg-green-500', icon: CheckCircle },
      ueberfaellig: { label: 'Überfällig', color: 'bg-red-500', icon: AlertTriangle },
      storniert: { label: 'Storniert', color: 'bg-gray-700', icon: XCircle },
      teilbezahlt: { label: 'Teilbezahlt', color: 'bg-amber-500', icon: CreditCard }
    };
    const c = config[status] || config.entwurf;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} text-white gap-1`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </Badge>
    );
  };

  // Invoice type label
  const getTypeLabel = (type) => {
    const labels = {
      rechnung: 'Rechnung',
      lieferschein: 'Lieferschein',
      stornorechnung: 'Stornorechnung'
    };
    return labels[type] || type;
  };

  // Add item to invoice
  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit: 'Stück', unit_price: 0, discount_percent: 0, tax_rate: formData.default_tax_rate }]
    });
  };

  // Remove item
  const removeItem = (idx) => {
    if (formData.items.length > 1) {
      setFormData({
        ...formData,
        items: formData.items.filter((_, i) => i !== idx)
      });
    }
  };

  // Update item
  const updateItem = (idx, field, value) => {
    const newItems = [...formData.items];
    newItems[idx][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let totalDiscount = 0;
    let taxBreakdown = {};

    formData.items.forEach(item => {
      const lineSubtotal = item.quantity * item.unit_price;
      const discountAmount = lineSubtotal * (item.discount_percent / 100);
      const netAmount = lineSubtotal - discountAmount;
      const taxAmount = netAmount * (item.tax_rate / 100);

      subtotal += lineSubtotal;
      totalDiscount += discountAmount;

      const taxKey = item.tax_rate.toString();
      if (!taxBreakdown[taxKey]) taxBreakdown[taxKey] = 0;
      taxBreakdown[taxKey] += taxAmount;
    });

    const netTotal = subtotal - totalDiscount;
    const taxTotal = Object.values(taxBreakdown).reduce((a, b) => a + b, 0);
    const grossTotal = netTotal + taxTotal;

    return { subtotal, totalDiscount, netTotal, taxBreakdown, taxTotal, grossTotal };
  };

  // Create invoice
  const handleCreate = async () => {
    // Check for customer (either selected or custom)
    if (!useCustomCustomer && !formData.customer_id) {
      toast.error('اختر العميل');
      return;
    }
    if (useCustomCustomer && !formData.custom_customer_name.trim()) {
      toast.error('أدخل اسم العميل');
      return;
    }
    if (formData.items.some(i => !i.description || i.quantity <= 0)) {
      toast.error('أكمل بيانات البنود');
      return;
    }

    setSaving(true);
    try {
      const submitData = { ...formData };
      if (useCustomCustomer) {
        submitData.customer_id = null;
      }
      const res = await axios.post(`${API_URL}/api/german-invoices`, submitData, { headers: getAuthHeader() });
      toast.success('تم إنشاء الفاتورة');
      setShowCreate(false);
      resetForm();
      setUseCustomCustomer(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ');
    } finally {
      setSaving(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      customer_id: '',
      custom_customer_name: '',
      custom_customer_address: '',
      invoice_type: 'rechnung',
      invoice_date: format(new Date(), 'yyyy-MM-dd'),
      due_date: format(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      payment_method: 'ueberweisung',
      default_tax_rate: 7,
      notes: '',
      internal_notes: '',
      items: [{ description: '', quantity: 1, unit: 'Stück', unit_price: 0, discount_percent: 0, tax_rate: 7 }]
    });
    setUseCustomCustomer(true); // Default to manual entry
  };

  // Change status
  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/german-invoices/${invoiceId}/status?status=${newStatus}`, {}, { headers: getAuthHeader() });
      toast.success('تم تحديث الحالة');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ');
    }
  };

  // Create Storno
  const handleStorno = async (invoiceId) => {
    if (!window.confirm('إنشاء فاتورة إلغاء (Stornorechnung)؟')) return;
    try {
      await axios.post(`${API_URL}/api/german-invoices/${invoiceId}/storno`, {}, { headers: getAuthHeader() });
      toast.success('تم إنشاء Stornorechnung');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ');
    }
  };

  // Record payment
  const handlePayment = async () => {
    if (!selectedInvoice || paymentForm.amount <= 0) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/german-invoices/${selectedInvoice.id}/payments`, {
        invoice_id: selectedInvoice.id,
        ...paymentForm
      }, { headers: getAuthHeader() });
      toast.success('تم تسجيل الدفعة');
      setShowPayment(false);
      setPaymentForm({ amount: 0, payment_date: format(new Date(), 'yyyy-MM-dd'), payment_method: 'bar', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ');
    } finally {
      setSaving(false);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/invoice-settings`, settingsForm, { headers: getAuthHeader() });
      toast.success('تم حفظ الإعدادات');
      setShowSettings(false);
      fetchData();
    } catch (error) {
      toast.error('خطأ في الحفظ');
    } finally {
      setSaving(false);
    }
  };

  // Preview invoice
  const openPreview = async (invoice) => {
    try {
      const res = await axios.get(`${API_URL}/api/german-invoices/${invoice.id}`, { headers: getAuthHeader() });
      setSelectedInvoice(res.data);
      setShowPreview(true);
    } catch (error) {
      toast.error('خطأ');
    }
  };

  // Open edit dialog
  const openEditDialog = async (invoice) => {
    try {
      const res = await axios.get(`${API_URL}/api/german-invoices/${invoice.id}`, { headers: getAuthHeader() });
      const inv = res.data;
      setEditingInvoice(inv);
      setFormData({
        customer_id: inv.customer_id,
        invoice_type: inv.invoice_type,
        invoice_date: inv.invoice_date,
        due_date: inv.due_date || '',
        payment_method: inv.payment_method,
        default_tax_rate: inv.default_tax_rate,
        notes: inv.notes || '',
        internal_notes: inv.internal_notes || '',
        items: inv.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          unit_price: item.unit_price,
          discount_percent: item.discount_percent || 0,
          tax_rate: item.tax_rate
        }))
      });
      setShowEdit(true);
    } catch (error) {
      toast.error('خطأ في تحميل الفاتورة');
    }
  };

  // Update invoice
  const handleUpdate = async () => {
    if (formData.items.some(i => !i.description || i.quantity <= 0)) {
      toast.error('أكمل بيانات البنود');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/german-invoices/${editingInvoice.id}`, formData, { headers: getAuthHeader() });
      toast.success('تم تحديث الفاتورة');
      setShowEdit(false);
      setEditingInvoice(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ');
    } finally {
      setSaving(false);
    }
  };

  // Print invoice using window.open (reliable A4 printing)
  const handlePrint = () => {
    if (!selectedInvoice) return;
    
    const fc = (n) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
    
    const itemsHTML = selectedInvoice.items?.map((item, idx) => `
      <tr style="background-color: ${idx % 2 === 0 ? '#f8fafc' : '#ffffff'}">
        <td style="padding: 2mm; text-align: center; border: 1px solid #e2e8f0">${idx + 1}</td>
        <td style="padding: 2mm; text-align: left; border: 1px solid #e2e8f0">${item.description}</td>
        <td style="padding: 2mm; text-align: right; border: 1px solid #e2e8f0">${item.quantity}</td>
        <td style="padding: 2mm; text-align: center; border: 1px solid #e2e8f0">${item.unit}</td>
        <td style="padding: 2mm; text-align: right; border: 1px solid #e2e8f0">${fc(item.unit_price)}</td>
        <td style="padding: 2mm; text-align: right; border: 1px solid #e2e8f0">${item.tax_rate}%</td>
        <td style="padding: 2mm; text-align: right; border: 1px solid #e2e8f0; font-weight: bold">${fc(item.gross_amount)}</td>
      </tr>
    `).join('') || '';
    
    const taxBreakdownHTML = Object.entries(selectedInvoice.tax_breakdown || {}).map(([rate, amount]) => `
      <tr>
        <td style="padding: 1.5mm 0; color: #475569">MwSt. ${rate}%:</td>
        <td style="padding: 1.5mm 0; text-align: right">${fc(amount)} €</td>
      </tr>
    `).join('');
    
    const printHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${getTypeLabel(selectedInvoice.invoice_type)} ${selectedInvoice.invoice_number}</title>
        <style>
          @page { size: A4; margin: 10mm; }
          body { font-family: Arial, sans-serif; font-size: 10pt; color: #1e293b; margin: 0; padding: 10mm; }
          * { box-sizing: border-box; }
        </style>
      </head>
      <body>
        <div style="max-width: 190mm; margin: 0 auto;">
          <!-- HEADER -->
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8mm; border-bottom: 2px solid #1e40af; padding-bottom: 5mm;">
            <div style="flex: 1;">
              ${selectedInvoice.logo_base64 ? `<img src="${selectedInvoice.logo_base64}" style="height: 20mm; max-width: 50mm; object-fit: contain; margin-bottom: 3mm;" />` : ''}
              <h1 style="font-size: 14pt; font-weight: bold; margin: 0 0 2mm 0; color: #1e293b;">${selectedInvoice.company_name || 'Firmenname'}</h1>
              <p style="font-size: 9pt; margin: 0; white-space: pre-line; color: #475569;">${selectedInvoice.company_address || ''}</p>
              ${selectedInvoice.company_phone ? `<p style="font-size: 9pt; margin: 0; color: #475569;">Tel: ${selectedInvoice.company_phone}</p>` : ''}
              ${selectedInvoice.company_email ? `<p style="font-size: 9pt; margin: 0; color: #475569;">E-Mail: ${selectedInvoice.company_email}</p>` : ''}
            </div>
            <div style="text-align: right;">
              <h2 style="font-size: 20pt; font-weight: bold; color: #1e40af; margin: 0 0 2mm 0;">${getTypeLabel(selectedInvoice.invoice_type).toUpperCase()}</h2>
              <p style="font-family: monospace; font-size: 14pt; font-weight: bold; margin: 0 0 3mm 0;">${selectedInvoice.invoice_number}</p>
              <table style="font-size: 9pt; margin-left: auto;">
                <tr>
                  <td style="text-align: right; padding-right: 3mm; color: #64748b;">Datum:</td>
                  <td style="font-weight: bold;">${selectedInvoice.invoice_date}</td>
                </tr>
                ${selectedInvoice.due_date ? `<tr><td style="text-align: right; padding-right: 3mm; color: #64748b;">Zahlungsziel:</td><td style="font-weight: bold;">${selectedInvoice.due_date}</td></tr>` : ''}
              </table>
            </div>
          </div>

          <!-- CUSTOMER -->
          <div style="margin-bottom: 8mm; padding: 4mm; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 2mm;">
            <p style="font-size: 8pt; color: #64748b; margin: 0 0 1mm 0;">Rechnungsempfänger:</p>
            <p style="font-size: 11pt; font-weight: bold; margin: 0 0 1mm 0;">${selectedInvoice.customer_name}</p>
            <p style="font-size: 9pt; margin: 0; white-space: pre-line;">${selectedInvoice.customer_address || ''}</p>
          </div>

          <!-- ITEMS TABLE -->
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 6mm; font-size: 9pt;">
            <thead>
              <tr style="background-color: #1e40af; color: white;">
                <th style="padding: 2.5mm; text-align: center; border: 1px solid #1e40af; width: 8%;">Pos.</th>
                <th style="padding: 2.5mm; text-align: left; border: 1px solid #1e40af; width: 35%;">Beschreibung</th>
                <th style="padding: 2.5mm; text-align: right; border: 1px solid #1e40af; width: 10%;">Menge</th>
                <th style="padding: 2.5mm; text-align: center; border: 1px solid #1e40af; width: 10%;">Einheit</th>
                <th style="padding: 2.5mm; text-align: right; border: 1px solid #1e40af; width: 12%;">Preis €</th>
                <th style="padding: 2.5mm; text-align: right; border: 1px solid #1e40af; width: 10%;">MwSt.</th>
                <th style="padding: 2.5mm; text-align: right; border: 1px solid #1e40af; width: 15%;">Betrag €</th>
              </tr>
            </thead>
            <tbody>${itemsHTML}</tbody>
          </table>

          <!-- TOTALS -->
          <div style="display: flex; justify-content: flex-end; margin-bottom: 6mm;">
            <table style="width: 60mm; font-size: 9pt;">
              <tr>
                <td style="padding: 1.5mm 0; color: #475569;">Nettobetrag:</td>
                <td style="padding: 1.5mm 0; text-align: right;">${fc(selectedInvoice.net_total)} €</td>
              </tr>
              ${taxBreakdownHTML}
              <tr style="border-top: 2px solid #1e40af;">
                <td style="padding: 2mm 0; font-weight: bold; font-size: 11pt;">Gesamtbetrag:</td>
                <td style="padding: 2mm 0; text-align: right; font-weight: bold; font-size: 11pt;">${fc(selectedInvoice.gross_total)} €</td>
              </tr>
              ${selectedInvoice.paid_amount > 0 ? `
                <tr>
                  <td style="padding: 1.5mm 0; color: #16a34a;">Bezahlt:</td>
                  <td style="padding: 1.5mm 0; text-align: right; color: #16a34a;">-${fc(selectedInvoice.paid_amount)} €</td>
                </tr>
                <tr style="border-top: 1px solid #e2e8f0;">
                  <td style="padding: 1.5mm 0; font-weight: bold; color: #dc2626;">Zu bezahlen:</td>
                  <td style="padding: 1.5mm 0; text-align: right; font-weight: bold; color: #dc2626;">${fc(selectedInvoice.remaining_amount)} €</td>
                </tr>
              ` : ''}
            </table>
          </div>

          <!-- BANK DETAILS -->
          ${selectedInvoice.iban ? `
            <div style="background-color: #f1f5f9; padding: 3mm; border-radius: 2mm; margin-bottom: 5mm; font-size: 8pt;">
              <p style="font-weight: bold; margin: 0 0 2mm 0;">Bankverbindung:</p>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1mm;">
                ${selectedInvoice.bank_name ? `<p style="margin: 0;">Bank: ${selectedInvoice.bank_name}</p>` : ''}
                <p style="margin: 0;">IBAN: ${selectedInvoice.iban}</p>
                ${selectedInvoice.bic ? `<p style="margin: 0;">BIC: ${selectedInvoice.bic}</p>` : ''}
                ${selectedInvoice.account_holder ? `<p style="margin: 0;">Empfänger: ${selectedInvoice.account_holder}</p>` : ''}
              </div>
            </div>
          ` : ''}

          <!-- NOTES -->
          ${selectedInvoice.notes ? `
            <div style="margin-bottom: 5mm; font-size: 9pt;">
              <p style="font-weight: bold; margin: 0 0 1mm 0;">Bemerkung:</p>
              <p style="margin: 0; color: #475569;">${selectedInvoice.notes}</p>
            </div>
          ` : ''}

          <!-- STAMP & SIGNATURE -->
          <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-top: 10mm; padding-top: 5mm; border-top: 1px dashed #cbd5e1;">
            <div style="text-align: center; width: 35mm;">
              ${selectedInvoice.stamp_base64 ? `<img src="${selectedInvoice.stamp_base64}" style="height: 25mm; max-width: 35mm; object-fit: contain;" />` : '<div style="height: 25mm; border: 1px dashed #cbd5e1; border-radius: 2mm; display: flex; align-items: center; justify-content: center; color: #94a3b8; font-size: 8pt;">Stempel</div>'}
              <p style="font-size: 7pt; color: #64748b; margin: 1mm 0 0 0;">Firmenstempel</p>
            </div>
            <div style="text-align: center; width: 45mm;">
              ${selectedInvoice.signature_base64 ? `<img src="${selectedInvoice.signature_base64}" style="height: 20mm; max-width: 45mm; object-fit: contain;" />` : '<div style="height: 20mm; border-bottom: 1px solid #1e293b;"></div>'}
              <p style="font-size: 8pt; margin: 1mm 0 0 0;">${selectedInvoice.geschaeftsfuehrer || 'Geschäftsführer'}</p>
              <p style="font-size: 7pt; color: #64748b; margin: 0;">Unterschrift</p>
            </div>
          </div>

          <!-- FOOTER -->
          <div style="margin-top: 8mm; padding-top: 3mm; border-top: 1px solid #e2e8f0; text-align: center; font-size: 8pt; color: #64748b;">
            <p style="margin: 0 0 1mm 0;">${selectedInvoice.footer_text || 'Vielen Dank für Ihren Auftrag!'}</p>
            <p style="margin: 0; font-size: 7pt;">
              ${selectedInvoice.tax_number ? `St.-Nr.: ${selectedInvoice.tax_number}` : ''}
              ${selectedInvoice.tax_number && selectedInvoice.ust_id ? ' | ' : ''}
              ${selectedInvoice.ust_id ? `USt-IdNr.: ${selectedInvoice.ust_id}` : ''}
            </p>
          </div>
        </div>

        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(printHTML);
      printWindow.document.close();
    }
  };

  // Filter invoices
  const filteredInvoices = invoices.filter(inv => {
    if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
    if (typeFilter !== 'all' && inv.invoice_type !== typeFilter) return false;
    return true;
  });

  const totals = calculateTotals();

  // Selected customer info
  const selectedCustomer = customers.find(c => c.id === formData.customer_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className={`p-4 md:p-6 space-y-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <Receipt className="w-7 h-7 text-amber-600" />
            {t('germanInvoices') || 'الفواتير الألمانية'}
          </h1>
          <p className="text-stone-500 text-sm">Rechnungen & Lieferscheine</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowSettings(true)} className="gap-2">
            <Settings className="w-4 h-4" />
            {t('settings') || 'الإعدادات'}
          </Button>
          <Button onClick={() => setShowCreate(true)} className="bg-amber-600 hover:bg-amber-700 gap-2">
            <Plus className="w-4 h-4" />
            {t('newInvoice') || 'فاتورة جديدة'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-blue-600">إجمالي الفواتير</p>
              <p className="text-xl font-bold text-blue-700">{summary.total_invoices}</p>
              <p className="text-xs text-blue-500">{fc(summary.total_amount)} €</p>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-green-600">المدفوعة</p>
              <p className="text-xl font-bold text-green-700">{summary.paid_count}</p>
              <p className="text-xs text-green-500">{fc(summary.paid_amount)} €</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-amber-600">المعلقة</p>
              <p className="text-xl font-bold text-amber-700">{summary.open_count + summary.partial_count}</p>
              <p className="text-xs text-amber-500">{fc(summary.pending_amount)} €</p>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-red-600">المتأخرة</p>
              <p className="text-xl font-bold text-red-700">{summary.overdue_count}</p>
              <p className="text-xs text-red-500">{fc(summary.overdue_amount)} €</p>
            </CardContent>
          </Card>
          <Card className="border-gray-200 bg-gray-50">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-gray-600">المسودات</p>
              <p className="text-xl font-bold text-gray-700">{summary.draft_count}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="الحالة" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">🔵 الكل ({invoices.length})</SelectItem>
            <SelectItem value="entwurf">📝 Entwurf - مسودة ({invoices.filter(i => i.status === 'entwurf').length})</SelectItem>
            <SelectItem value="offen">📤 Offen - مرسلة ({invoices.filter(i => i.status === 'offen').length})</SelectItem>
            <SelectItem value="teilbezahlt">💳 Teilbezahlt - جزئي ({invoices.filter(i => i.status === 'teilbezahlt').length})</SelectItem>
            <SelectItem value="bezahlt">✅ Bezahlt - مدفوعة ({invoices.filter(i => i.status === 'bezahlt').length})</SelectItem>
            <SelectItem value="ueberfaellig">⚠️ Überfällig - متأخرة ({invoices.filter(i => i.status === 'ueberfaellig').length})</SelectItem>
            <SelectItem value="storniert">❌ Storniert - ملغية ({invoices.filter(i => i.status === 'storniert').length})</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="النوع" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">الكل</SelectItem>
            <SelectItem value="rechnung">Rechnung</SelectItem>
            <SelectItem value="lieferschein">Lieferschein</SelectItem>
            <SelectItem value="stornorechnung">Stornorechnung</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Quick Filter Buttons */}
        <div className="flex gap-1 border-r pr-3 mr-2">
          <Button
            variant={statusFilter === 'offen' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(statusFilter === 'offen' ? 'all' : 'offen')}
            className="gap-1"
          >
            📤 مرسلة
          </Button>
          <Button
            variant={statusFilter === 'ueberfaellig' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(statusFilter === 'ueberfaellig' ? 'all' : 'ueberfaellig')}
            className="gap-1 text-red-600 border-red-200"
          >
            ⚠️ متأخرة
          </Button>
          <Button
            variant={statusFilter === 'bezahlt' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(statusFilter === 'bezahlt' ? 'all' : 'bezahlt')}
            className="gap-1 text-green-600 border-green-200"
          >
            ✅ مدفوعة
          </Button>
        </div>
      </div>

      {/* Invoices Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>رقم الفاتورة</TableHead>
                <TableHead>النوع</TableHead>
                <TableHead>العميل</TableHead>
                <TableHead>التاريخ</TableHead>
                <TableHead>المبلغ</TableHead>
                <TableHead>المدفوع</TableHead>
                <TableHead>الحالة</TableHead>
                <TableHead className="text-center">إجراءات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-stone-500">
                    لا توجد فواتير
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvoices.map(inv => (
                  <TableRow key={inv.id} className="hover:bg-stone-50">
                    <TableCell className="font-mono font-bold text-blue-600">{inv.invoice_number}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{getTypeLabel(inv.invoice_type)}</Badge>
                    </TableCell>
                    <TableCell>{inv.customer_name}</TableCell>
                    <TableCell>{inv.invoice_date}</TableCell>
                    <TableCell className="font-bold">{fc(inv.gross_total)} €</TableCell>
                    <TableCell className="text-green-600">{fc(inv.paid_amount)} €</TableCell>
                    <TableCell>{getStatusBadge(inv.status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-center">
                        <Button variant="ghost" size="sm" onClick={() => openPreview(inv)} title="عرض">
                          <Eye className="w-4 h-4" />
                        </Button>
                        {/* Print Button - Direct to A4 Print Page */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/invoice-print/${inv.id}`)}
                          title="طباعة A4"
                          className="text-blue-600"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        {/* Edit Button - Available for all except storniert */}
                        {inv.status !== 'storniert' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(inv)}
                            title="تعديل"
                            className="text-amber-600"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        {inv.status !== 'storniert' && inv.status !== 'bezahlt' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedInvoice(inv); setPaymentForm({ ...paymentForm, amount: inv.remaining_amount }); setShowPayment(true); }}
                              title="تسجيل دفعة"
                              className="text-green-600"
                            >
                              <CreditCard className="w-4 h-4" />
                            </Button>
                            {inv.status === 'entwurf' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStatusChange(inv.id, 'offen')}
                                title="إصدار"
                                className="text-blue-600"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStorno(inv.id)}
                              title="Stornieren"
                              className="text-red-600"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Invoice Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" />
              إنشاء فاتورة جديدة - Neue Rechnung
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>نوع المستند</Label>
                <Select value={formData.invoice_type} onValueChange={(v) => setFormData({ ...formData, invoice_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rechnung">Rechnung (فاتورة)</SelectItem>
                    <SelectItem value="lieferschein">Lieferschein (إيصال توصيل)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>رقم الفاتورة (اختياري)</Label>
                <Input 
                  value={formData.invoice_number} 
                  onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  placeholder="يُنشأ تلقائياً إذا تُرك فارغاً"
                  className="font-mono"
                />
                <p className="text-xs text-stone-500">اتركه فارغاً لإنشاء رقم تلقائي، أو أدخل رقماً مخصصاً</p>
              </div>
              <div className="space-y-2">
                <Label>تاريخ الفاتورة</Label>
                <Input type="date" value={formData.invoice_date} onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>تاريخ الاستحقاق (Zahlungsziel)</Label>
                <Input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
              </div>
            </div>

            {/* Customer Selection */}
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <Label className="font-bold">العميل (Kunde)</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="customCustomer"
                    checked={!useCustomCustomer}
                    onChange={(e) => {
                      setUseCustomCustomer(!e.target.checked);
                      if (!e.target.checked) {
                        setFormData({ ...formData, customer_id: '' });
                      } else {
                        setFormData({ ...formData, custom_customer_name: '', custom_customer_address: '' });
                      }
                    }}
                    className="w-4 h-4"
                  />
                  <label htmlFor="customCustomer" className="text-sm text-stone-600 cursor-pointer">
                    اختيار من العملاء المحفوظين
                  </label>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!useCustomCustomer ? (
                  <div className="space-y-2">
                    <Label>اختر من القائمة</Label>
                    <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="اختر العميل" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name} - {c.shop_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>اسم العميل *</Label>
                      <Input
                        value={formData.custom_customer_name}
                        onChange={(e) => setFormData({ ...formData, custom_customer_name: e.target.value })}
                        placeholder="أدخل اسم العميل"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>عنوان العميل</Label>
                      <Input
                        value={formData.custom_customer_address}
                        onChange={(e) => setFormData({ ...formData, custom_customer_address: e.target.value })}
                        placeholder="العنوان (اختياري)"
                      />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <Label>طريقة الدفع (Zahlungsart)</Label>
                  <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bar">Bar (نقدي)</SelectItem>
                      <SelectItem value="ueberweisung">Überweisung (تحويل)</SelectItem>
                      <SelectItem value="lastschrift">Lastschrift</SelectItem>
                      <SelectItem value="kreditkarte">Kreditkarte</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Customer Info Display */}
            {selectedCustomer && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <User className="w-8 h-8 text-blue-600" />
                    <div>
                      <p className="font-bold">{selectedCustomer.name}</p>
                      <p className="text-sm text-stone-600">{selectedCustomer.shop_name}</p>
                      <p className="text-sm text-stone-500">{selectedCustomer.address}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tax Rate */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>نسبة الضريبة الافتراضية (MwSt.)</Label>
                <Select value={formData.default_tax_rate.toString()} onValueChange={(v) => setFormData({ ...formData, default_tax_rate: parseFloat(v) })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0% (Steuerfrei)</SelectItem>
                    <SelectItem value="7">7% (ermäßigt)</SelectItem>
                    <SelectItem value="19">19% (regulär)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Line Items */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-lg font-bold">البنود (Positionen)</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem} className="gap-1">
                  <Plus className="w-4 h-4" /> إضافة بند
                </Button>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-stone-100">
                      <TableHead className="w-[250px]">الوصف (Beschreibung)</TableHead>
                      <TableHead className="w-[80px]">الكمية</TableHead>
                      <TableHead className="w-[80px]">الوحدة</TableHead>
                      <TableHead className="w-[100px]">السعر €</TableHead>
                      <TableHead className="w-[80px]">خصم %</TableHead>
                      <TableHead className="w-[80px]">MwSt.</TableHead>
                      <TableHead className="w-[100px]">المجموع</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.items.map((item, idx) => {
                      const lineTotal = item.quantity * item.unit_price;
                      const discount = lineTotal * (item.discount_percent / 100);
                      const net = lineTotal - discount;
                      const tax = net * (item.tax_rate / 100);
                      const gross = net + tax;
                      return (
                        <TableRow key={idx}>
                          <TableCell>
                            <Input
                              value={item.description}
                              onChange={(e) => updateItem(idx, 'description', e.target.value)}
                              placeholder="وصف المنتج..."
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={item.quantity}
                              onChange={(e) => {
                                const val = e.target.value.replace(',', '.');
                                if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                  updateItem(idx, 'quantity', val === '' ? 0 : parseFloat(val) || 0);
                                }
                              }}
                              className="text-center"
                              placeholder="0"
                            />
                          </TableCell>
                          <TableCell>
                            <Select value={item.unit} onValueChange={(v) => updateItem(idx, 'unit', v)}>
                              <SelectTrigger className="h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Stück">Stück</SelectItem>
                                <SelectItem value="Karton">Karton</SelectItem>
                                <SelectItem value="kg">kg</SelectItem>
                                <SelectItem value="Liter">Liter</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={item.unit_price}
                              onChange={(e) => {
                                const val = e.target.value.replace(',', '.');
                                if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                  updateItem(idx, 'unit_price', val === '' ? 0 : parseFloat(val) || 0);
                                }
                              }}
                              className="text-center"
                              placeholder="0.00"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={item.discount_percent}
                              onChange={(e) => {
                                const val = e.target.value.replace(',', '.');
                                if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                  const num = parseFloat(val) || 0;
                                  if (num <= 100) {
                                    updateItem(idx, 'discount_percent', num);
                                  }
                                }
                              }}
                              className="text-center"
                              placeholder="0"
                            />
                          </TableCell>
                          <TableCell>
                            <Select value={item.tax_rate.toString()} onValueChange={(v) => updateItem(idx, 'tax_rate', parseFloat(v))}>
                              <SelectTrigger className="h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">0%</SelectItem>
                                <SelectItem value="7">7%</SelectItem>
                                <SelectItem value="19">19%</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="font-bold text-blue-600">
                            {fc(gross)} €
                          </TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(idx)}
                              className="text-red-500 hover:text-red-700"
                              disabled={formData.items.length === 1}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-72 space-y-2 bg-stone-50 p-4 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Zwischensumme:</span>
                    <span>{fc(totals.subtotal)} €</span>
                  </div>
                  {totals.totalDiscount > 0 && (
                    <div className="flex justify-between text-sm text-red-600">
                      <span>Rabatt:</span>
                      <span>-{fc(totals.totalDiscount)} €</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm border-t pt-2">
                    <span>Nettobetrag:</span>
                    <span>{fc(totals.netTotal)} €</span>
                  </div>
                  {Object.entries(totals.taxBreakdown).map(([rate, amount]) => (
                    <div key={rate} className="flex justify-between text-sm text-stone-600">
                      <span>MwSt. {rate}%:</span>
                      <span>{fc(amount)} €</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Gesamtbetrag:</span>
                    <span className="text-amber-600">{fc(totals.grossTotal)} €</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>ملاحظات للعميل (Bemerkung)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="ملاحظات تظهر على الفاتورة..."
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>ملاحظات داخلية</Label>
                <Textarea
                  value={formData.internal_notes}
                  onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })}
                  placeholder="ملاحظات داخلية (لا تظهر للعميل)..."
                  rows={2}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowCreate(false)}>إلغاء</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-amber-600 hover:bg-amber-700 gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              إنشاء الفاتورة
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Dialog */}
      <Dialog open={showEdit} onOpenChange={(open) => { setShowEdit(open); if (!open) { setEditingInvoice(null); resetForm(); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-600" />
              تعديل الفاتورة - {editingInvoice?.invoice_number}
              <Badge variant="outline" className="mr-2">{editingInvoice?.status}</Badge>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Edit History Notice */}
            {editingInvoice?.edit_history?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <p className="font-bold text-amber-800">📝 سجل التعديلات ({editingInvoice.edit_history.length})</p>
                <p className="text-amber-700 text-xs">آخر تعديل: {editingInvoice.edit_history[editingInvoice.edit_history.length - 1]?.edited_at?.split('T')[0]} بواسطة {editingInvoice.edit_history[editingInvoice.edit_history.length - 1]?.edited_by}</p>
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>تاريخ الفاتورة</Label>
                <Input type="date" value={formData.invoice_date} onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>تاريخ الاستحقاق (Zahlungsziel)</Label>
                <Input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>طريقة الدفع</Label>
                <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bar">Bar (نقدي)</SelectItem>
                    <SelectItem value="ueberweisung">Überweisung (تحويل)</SelectItem>
                    <SelectItem value="lastschrift">Lastschrift</SelectItem>
                    <SelectItem value="kreditkarte">Kreditkarte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Line Items */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-lg font-bold">البنود (Positionen)</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem} className="gap-1">
                  <Plus className="w-4 h-4" /> إضافة بند
                </Button>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-stone-100">
                      <TableHead className="w-[250px]">الوصف</TableHead>
                      <TableHead className="w-[80px]">الكمية</TableHead>
                      <TableHead className="w-[80px]">الوحدة</TableHead>
                      <TableHead className="w-[100px]">السعر €</TableHead>
                      <TableHead className="w-[80px]">خصم %</TableHead>
                      <TableHead className="w-[80px]">MwSt.</TableHead>
                      <TableHead className="w-[100px]">المجموع</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.items.map((item, idx) => {
                      const lineTotal = item.quantity * item.unit_price;
                      const discount = lineTotal * (item.discount_percent / 100);
                      const net = lineTotal - discount;
                      const tax = net * (item.tax_rate / 100);
                      const gross = net + tax;
                      return (
                        <TableRow key={idx}>
                          <TableCell>
                            <Input value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} placeholder="وصف المنتج..." />
                          </TableCell>
                          <TableCell>
                            <Input type="number" min="0" step="0.01" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)} className="text-center" />
                          </TableCell>
                          <TableCell>
                            <Select value={item.unit} onValueChange={(v) => updateItem(idx, 'unit', v)}>
                              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Stück">Stück</SelectItem>
                                <SelectItem value="Karton">Karton</SelectItem>
                                <SelectItem value="kg">kg</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input type="number" min="0" step="0.01" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} className="text-center" />
                          </TableCell>
                          <TableCell>
                            <Input type="number" min="0" max="100" value={item.discount_percent} onChange={(e) => updateItem(idx, 'discount_percent', parseFloat(e.target.value) || 0)} className="text-center" />
                          </TableCell>
                          <TableCell>
                            <Select value={item.tax_rate.toString()} onValueChange={(v) => updateItem(idx, 'tax_rate', parseFloat(v))}>
                              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">0%</SelectItem>
                                <SelectItem value="7">7%</SelectItem>
                                <SelectItem value="19">19%</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="font-bold text-blue-600">{fc(gross)} €</TableCell>
                          <TableCell>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(idx)} className="text-red-500" disabled={formData.items.length === 1}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-72 space-y-2 bg-stone-50 p-4 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Nettobetrag:</span>
                    <span>{fc(totals.netTotal)} €</span>
                  </div>
                  {Object.entries(totals.taxBreakdown).map(([rate, amount]) => (
                    <div key={rate} className="flex justify-between text-sm text-stone-600">
                      <span>MwSt. {rate}%:</span>
                      <span>{fc(amount)} €</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Gesamtbetrag:</span>
                    <span className="text-amber-600">{fc(totals.grossTotal)} €</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>ملاحظات للعميل (Bemerkung)</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>ملاحظات داخلية</Label>
                <Textarea value={formData.internal_notes} onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })} rows={2} />
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => { setShowEdit(false); setEditingInvoice(null); resetForm(); }}>إلغاء</Button>
            <Button onClick={handleUpdate} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              حفظ التعديلات
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Preview Dialog - A4 Format */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-[900px] max-h-[95vh] overflow-y-auto p-0 print:max-w-none print:max-h-none print:overflow-visible">
          <DialogHeader className="no-print p-4 border-b sticky top-0 bg-white z-10">
            <DialogTitle className="flex items-center justify-between">
              <span>معاينة الفاتورة - {selectedInvoice?.invoice_number}</span>
              <div className="flex gap-2">
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={() => {
                    setShowPreview(false);
                    navigate(`/invoice-print/${selectedInvoice?.id}`);
                  }} 
                  className="gap-1 no-print bg-blue-600 hover:bg-blue-700"
                >
                  <Printer className="w-4 h-4" /> طباعة A4
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          {selectedInvoice && (
            <div 
              ref={printRef} 
              className="invoice-a4-container bg-white mx-auto shadow-lg print:shadow-none"
              style={{ 
                fontFamily: 'Arial, Helvetica, sans-serif',
                width: '210mm',
                minHeight: '297mm',
                padding: '15mm 20mm',
                boxSizing: 'border-box',
                fontSize: '10pt',
                lineHeight: '1.4',
                position: 'relative'
              }}
            >
              {/* ===== HEADER WITH LOGO ===== */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8mm', borderBottom: '2px solid #1e40af', paddingBottom: '5mm' }}>
                {/* Company Info with Logo */}
                <div style={{ flex: 1 }}>
                  {selectedInvoice.logo_base64 && (
                    <img 
                      src={selectedInvoice.logo_base64} 
                      alt="Logo" 
                      style={{ height: '20mm', maxWidth: '50mm', objectFit: 'contain', marginBottom: '3mm' }}
                    />
                  )}
                  <h1 style={{ fontSize: '14pt', fontWeight: 'bold', margin: '0 0 2mm 0', color: '#1e293b' }}>
                    {selectedInvoice.company_name || 'Firmenname'}
                  </h1>
                  <p style={{ fontSize: '9pt', margin: 0, whiteSpace: 'pre-line', color: '#475569' }}>
                    {selectedInvoice.company_address}
                  </p>
                  {selectedInvoice.company_phone && (
                    <p style={{ fontSize: '9pt', margin: 0, color: '#475569' }}>Tel: {selectedInvoice.company_phone}</p>
                  )}
                  {selectedInvoice.company_email && (
                    <p style={{ fontSize: '9pt', margin: 0, color: '#475569' }}>E-Mail: {selectedInvoice.company_email}</p>
                  )}
                </div>

                {/* Invoice Type & Number */}
                <div style={{ textAlign: 'right' }}>
                  <h2 style={{ fontSize: '20pt', fontWeight: 'bold', color: '#1e40af', margin: '0 0 2mm 0' }}>
                    {getTypeLabel(selectedInvoice.invoice_type).toUpperCase()}
                  </h2>
                  <p style={{ fontFamily: 'monospace', fontSize: '14pt', fontWeight: 'bold', margin: '0 0 3mm 0' }}>
                    {selectedInvoice.invoice_number}
                  </p>
                  <table style={{ fontSize: '9pt', marginLeft: 'auto' }}>
                    <tbody>
                      <tr>
                        <td style={{ textAlign: 'right', paddingRight: '3mm', color: '#64748b' }}>Datum:</td>
                        <td style={{ fontWeight: 'bold' }}>{selectedInvoice.invoice_date}</td>
                      </tr>
                      {selectedInvoice.due_date && (
                        <tr>
                          <td style={{ textAlign: 'right', paddingRight: '3mm', color: '#64748b' }}>Zahlungsziel:</td>
                          <td style={{ fontWeight: 'bold' }}>{selectedInvoice.due_date}</td>
                        </tr>
                      )}
                      {selectedInvoice.customer_number && (
                        <tr>
                          <td style={{ textAlign: 'right', paddingRight: '3mm', color: '#64748b' }}>Kunden-Nr.:</td>
                          <td style={{ fontWeight: 'bold' }}>{selectedInvoice.customer_number}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ===== CUSTOMER ADDRESS ===== */}
              <div style={{ marginBottom: '8mm', padding: '4mm', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '2mm' }}>
                <p style={{ fontSize: '8pt', color: '#64748b', margin: '0 0 1mm 0' }}>Rechnungsempfänger:</p>
                <p style={{ fontSize: '11pt', fontWeight: 'bold', margin: '0 0 1mm 0' }}>{selectedInvoice.customer_name}</p>
                <p style={{ fontSize: '9pt', margin: 0, whiteSpace: 'pre-line' }}>{selectedInvoice.customer_address}</p>
                {selectedInvoice.customer_tax_number && (
                  <p style={{ fontSize: '8pt', margin: '2mm 0 0 0', color: '#475569' }}>USt-IdNr.: {selectedInvoice.customer_tax_number}</p>
                )}
              </div>

              {/* ===== ITEMS TABLE ===== */}
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '6mm', fontSize: '9pt' }}>
                <thead>
                  <tr style={{ backgroundColor: '#1e40af', color: 'white' }}>
                    <th style={{ padding: '2.5mm', textAlign: 'center', border: '1px solid #1e40af', width: '8%' }}>Pos.</th>
                    <th style={{ padding: '2.5mm', textAlign: 'left', border: '1px solid #1e40af', width: '35%' }}>Beschreibung</th>
                    <th style={{ padding: '2.5mm', textAlign: 'right', border: '1px solid #1e40af', width: '10%' }}>Menge</th>
                    <th style={{ padding: '2.5mm', textAlign: 'center', border: '1px solid #1e40af', width: '10%' }}>Einheit</th>
                    <th style={{ padding: '2.5mm', textAlign: 'right', border: '1px solid #1e40af', width: '12%' }}>Preis €</th>
                    <th style={{ padding: '2.5mm', textAlign: 'right', border: '1px solid #1e40af', width: '10%' }}>MwSt.</th>
                    <th style={{ padding: '2.5mm', textAlign: 'right', border: '1px solid #1e40af', width: '15%' }}>Betrag €</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedInvoice.items?.map((item, idx) => (
                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#f8fafc' : '#ffffff' }}>
                      <td style={{ padding: '2mm', textAlign: 'center', border: '1px solid #e2e8f0' }}>{idx + 1}</td>
                      <td style={{ padding: '2mm', textAlign: 'left', border: '1px solid #e2e8f0' }}>{item.description}</td>
                      <td style={{ padding: '2mm', textAlign: 'right', border: '1px solid #e2e8f0' }}>{item.quantity}</td>
                      <td style={{ padding: '2mm', textAlign: 'center', border: '1px solid #e2e8f0' }}>{item.unit}</td>
                      <td style={{ padding: '2mm', textAlign: 'right', border: '1px solid #e2e8f0' }}>{fc(item.unit_price)}</td>
                      <td style={{ padding: '2mm', textAlign: 'right', border: '1px solid #e2e8f0' }}>{item.tax_rate}%</td>
                      <td style={{ padding: '2mm', textAlign: 'right', border: '1px solid #e2e8f0', fontWeight: 'bold' }}>{fc(item.gross_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* ===== TOTALS ===== */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6mm' }}>
                <table style={{ width: '60mm', fontSize: '9pt' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '1.5mm 0', color: '#475569' }}>Nettobetrag:</td>
                      <td style={{ padding: '1.5mm 0', textAlign: 'right' }}>{fc(selectedInvoice.net_total)} €</td>
                    </tr>
                    {Object.entries(selectedInvoice.tax_breakdown || {}).map(([rate, amount]) => (
                      <tr key={rate}>
                        <td style={{ padding: '1.5mm 0', color: '#475569' }}>MwSt. {rate}%:</td>
                        <td style={{ padding: '1.5mm 0', textAlign: 'right' }}>{fc(amount)} €</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '2px solid #1e40af' }}>
                      <td style={{ padding: '2mm 0', fontWeight: 'bold', fontSize: '11pt' }}>Gesamtbetrag:</td>
                      <td style={{ padding: '2mm 0', textAlign: 'right', fontWeight: 'bold', fontSize: '11pt' }}>{fc(selectedInvoice.gross_total)} €</td>
                    </tr>
                    {selectedInvoice.paid_amount > 0 && (
                      <>
                        <tr>
                          <td style={{ padding: '1.5mm 0', color: '#16a34a' }}>Bezahlt:</td>
                          <td style={{ padding: '1.5mm 0', textAlign: 'right', color: '#16a34a' }}>-{fc(selectedInvoice.paid_amount)} €</td>
                        </tr>
                        <tr style={{ borderTop: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '1.5mm 0', fontWeight: 'bold', color: '#dc2626' }}>Zu bezahlen:</td>
                          <td style={{ padding: '1.5mm 0', textAlign: 'right', fontWeight: 'bold', color: '#dc2626' }}>{fc(selectedInvoice.remaining_amount)} €</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>

              {/* ===== BANK DETAILS ===== */}
              {selectedInvoice.iban && (
                <div style={{ backgroundColor: '#f1f5f9', padding: '3mm', borderRadius: '2mm', marginBottom: '5mm', fontSize: '8pt' }}>
                  <p style={{ fontWeight: 'bold', margin: '0 0 2mm 0' }}>Bankverbindung:</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1mm' }}>
                    {selectedInvoice.bank_name && <p style={{ margin: 0 }}>Bank: {selectedInvoice.bank_name}</p>}
                    <p style={{ margin: 0 }}>IBAN: {selectedInvoice.iban}</p>
                    {selectedInvoice.bic && <p style={{ margin: 0 }}>BIC: {selectedInvoice.bic}</p>}
                    {selectedInvoice.account_holder && <p style={{ margin: 0 }}>Empfänger: {selectedInvoice.account_holder}</p>}
                  </div>
                </div>
              )}

              {/* ===== NOTES ===== */}
              {selectedInvoice.notes && (
                <div style={{ marginBottom: '5mm', fontSize: '9pt' }}>
                  <p style={{ fontWeight: 'bold', margin: '0 0 1mm 0' }}>Bemerkung:</p>
                  <p style={{ margin: 0, color: '#475569' }}>{selectedInvoice.notes}</p>
                </div>
              )}

              {/* ===== STAMP AND SIGNATURE SECTION ===== */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '10mm', paddingTop: '5mm', borderTop: '1px dashed #cbd5e1' }}>
                {/* Stamp */}
                <div style={{ textAlign: 'center', width: '35mm' }}>
                  {selectedInvoice.stamp_base64 ? (
                    <img 
                      src={selectedInvoice.stamp_base64} 
                      alt="Stempel" 
                      style={{ height: '25mm', maxWidth: '35mm', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{ height: '25mm', border: '1px dashed #cbd5e1', borderRadius: '2mm', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '8pt' }}>
                      Stempel
                    </div>
                  )}
                  <p style={{ fontSize: '7pt', color: '#64748b', margin: '1mm 0 0 0' }}>Firmenstempel</p>
                </div>

                {/* Signature */}
                <div style={{ textAlign: 'center', width: '45mm' }}>
                  {selectedInvoice.signature_base64 ? (
                    <img 
                      src={selectedInvoice.signature_base64} 
                      alt="Unterschrift" 
                      style={{ height: '20mm', maxWidth: '45mm', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{ height: '20mm', borderBottom: '1px solid #1e293b' }}></div>
                  )}
                  <p style={{ fontSize: '8pt', margin: '1mm 0 0 0' }}>
                    {selectedInvoice.geschaeftsfuehrer || 'Geschäftsführer'}
                  </p>
                  <p style={{ fontSize: '7pt', color: '#64748b', margin: 0 }}>Unterschrift</p>
                </div>
              </div>

              {/* ===== FOOTER ===== */}
              <div style={{ marginTop: '8mm', paddingTop: '3mm', borderTop: '1px solid #e2e8f0', textAlign: 'center', fontSize: '8pt', color: '#64748b' }}>
                <p style={{ margin: '0 0 1mm 0' }}>{selectedInvoice.footer_text || 'Vielen Dank für Ihren Auftrag!'}</p>
                <p style={{ margin: 0, fontSize: '7pt' }}>
                  {selectedInvoice.tax_number && <span>St.-Nr.: {selectedInvoice.tax_number}</span>}
                  {selectedInvoice.tax_number && selectedInvoice.ust_id && <span> | </span>}
                  {selectedInvoice.ust_id && <span>USt-IdNr.: {selectedInvoice.ust_id}</span>}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-600" />
              تسجيل دفعة - {selectedInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-600">المبلغ المتبقي: <span className="font-bold">{fc(selectedInvoice?.remaining_amount)} €</span></p>
            </div>
            <div className="space-y-2">
              <Label>مبلغ الدفعة (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>تاريخ الدفع</Label>
              <Input
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>طريقة الدفع</Label>
              <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">Bar (نقدي)</SelectItem>
                  <SelectItem value="ueberweisung">Überweisung</SelectItem>
                  <SelectItem value="lastschrift">Lastschrift</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>ملاحظات</Label>
              <Input
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowPayment(false)}>إلغاء</Button>
              <Button onClick={handlePayment} disabled={saving} className="bg-green-600 hover:bg-green-700 gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                تسجيل الدفعة
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-600" />
              إعدادات الفواتير - Rechnungseinstellungen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Company Info */}
            <div className="space-y-4">
              <h3 className="font-bold text-lg border-b pb-2">بيانات الشركة</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>اسم الشركة (Firmenname)</Label>
                  <Input
                    value={settingsForm.company_name}
                    onChange={(e) => setSettingsForm({ ...settingsForm, company_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>المدير (Geschäftsführer)</Label>
                  <Input
                    value={settingsForm.geschaeftsfuehrer}
                    onChange={(e) => setSettingsForm({ ...settingsForm, geschaeftsfuehrer: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>العنوان (Adresse)</Label>
                <Textarea
                  value={settingsForm.company_address}
                  onChange={(e) => setSettingsForm({ ...settingsForm, company_address: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>الهاتف</Label>
                  <Input
                    value={settingsForm.company_phone}
                    onChange={(e) => setSettingsForm({ ...settingsForm, company_phone: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>البريد الإلكتروني</Label>
                  <Input
                    value={settingsForm.company_email}
                    onChange={(e) => setSettingsForm({ ...settingsForm, company_email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>الموقع</Label>
                  <Input
                    value={settingsForm.company_website}
                    onChange={(e) => setSettingsForm({ ...settingsForm, company_website: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Tax Info */}
            <div className="space-y-4">
              <h3 className="font-bold text-lg border-b pb-2">البيانات الضريبية</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>الرقم الضريبي (St.-Nr.)</Label>
                  <Input
                    value={settingsForm.tax_number}
                    onChange={(e) => setSettingsForm({ ...settingsForm, tax_number: e.target.value })}
                    placeholder="123/456/78901"
                  />
                </div>
                <div className="space-y-2">
                  <Label>رقم ضريبة القيمة المضافة (USt-IdNr.)</Label>
                  <Input
                    value={settingsForm.ust_id}
                    onChange={(e) => setSettingsForm({ ...settingsForm, ust_id: e.target.value })}
                    placeholder="DE123456789"
                  />
                </div>
              </div>
            </div>

            {/* Bank Info */}
            <div className="space-y-4">
              <h3 className="font-bold text-lg border-b pb-2">البيانات البنكية</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>اسم البنك (Bankname)</Label>
                  <Input
                    value={settingsForm.bank_name}
                    onChange={(e) => setSettingsForm({ ...settingsForm, bank_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>مستلم الدفع (Zahlungsempfänger)</Label>
                  <Input
                    value={settingsForm.account_holder}
                    onChange={(e) => setSettingsForm({ ...settingsForm, account_holder: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>IBAN</Label>
                  <Input
                    value={settingsForm.iban}
                    onChange={(e) => setSettingsForm({ ...settingsForm, iban: e.target.value })}
                    placeholder="DE89 3704 0044 0532 0130 00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>BIC</Label>
                  <Input
                    value={settingsForm.bic}
                    onChange={(e) => setSettingsForm({ ...settingsForm, bic: e.target.value })}
                    placeholder="COBADEFFXXX"
                  />
                </div>
              </div>
            </div>

            {/* Other Settings */}
            <div className="space-y-4">
              <h3 className="font-bold text-lg border-b pb-2">إعدادات أخرى</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>بادئة رقم الفاتورة</Label>
                  <Input
                    value={settingsForm.invoice_prefix}
                    onChange={(e) => setSettingsForm({ ...settingsForm, invoice_prefix: e.target.value })}
                    placeholder="RE"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>نص التذييل (Footer)</Label>
                <Textarea
                  value={settingsForm.footer_text}
                  onChange={(e) => setSettingsForm({ ...settingsForm, footer_text: e.target.value })}
                  placeholder="Vielen Dank für Ihren Auftrag!"
                  rows={2}
                />
              </div>
            </div>

            {/* Logo, Stamp, Signature */}
            <div className="space-y-4">
              <h3 className="font-bold text-lg border-b pb-2">الشعار والختم والتوقيع</h3>
              
              {/* Logo Upload */}
              <div className="space-y-2">
                <Label>شعار الشركة (Logo) - يظهر في ترويسة الفاتورة</Label>
                <div className="flex gap-4 items-start">
                  <div className="flex-1">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setSettingsForm({ ...settingsForm, logo_base64: ev.target.result });
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-stone-500 mt-1">PNG أو JPG، الحجم المثالي: 200×60 بكسل</p>
                  </div>
                  {settingsForm.logo_base64 && (
                    <div className="relative">
                      <img src={settingsForm.logo_base64} alt="Logo" className="h-12 border rounded p-1" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setSettingsForm({ ...settingsForm, logo_base64: '' })}
                        className="absolute -top-2 -right-2 h-6 w-6 p-0 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        ×
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Stamp Upload */}
              <div className="space-y-2">
                <Label>ختم الشركة (Firmenstempel) - يظهر في نهاية الفاتورة</Label>
                <div className="flex gap-4 items-start">
                  <div className="flex-1">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setSettingsForm({ ...settingsForm, stamp_base64: ev.target.result });
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-stone-500 mt-1">PNG مع خلفية شفافة، الحجم المثالي: 100×100 بكسل</p>
                  </div>
                  {settingsForm.stamp_base64 && (
                    <div className="relative">
                      <img src={settingsForm.stamp_base64} alt="Stamp" className="h-16 border rounded p-1 bg-gray-50" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setSettingsForm({ ...settingsForm, stamp_base64: '' })}
                        className="absolute -top-2 -right-2 h-6 w-6 p-0 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        ×
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Signature Upload */}
              <div className="space-y-2">
                <Label>توقيع المدير (Unterschrift) - يظهر في نهاية الفاتورة</Label>
                <div className="flex gap-4 items-start">
                  <div className="flex-1">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setSettingsForm({ ...settingsForm, signature_base64: ev.target.result });
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-stone-500 mt-1">PNG مع خلفية شفافة، الحجم المثالي: 150×50 بكسل</p>
                  </div>
                  {settingsForm.signature_base64 && (
                    <div className="relative">
                      <img src={settingsForm.signature_base64} alt="Signature" className="h-10 border rounded p-1 bg-gray-50" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setSettingsForm({ ...settingsForm, signature_base64: '' })}
                        className="absolute -top-2 -right-2 h-6 w-6 p-0 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        ×
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowSettings(false)}>إلغاء</Button>
            <Button onClick={handleSaveSettings} disabled={saving} className="bg-amber-600 hover:bg-amber-700 gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              حفظ الإعدادات
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GermanInvoicesPage;
